(function () {
  'use strict';
  // Global Localization Engine v2.4.1 - Instant Multi-Language & Multi-Currency
  // ── 0. SINGLETON GUARD & ENVIRONMENT AWARENESS ─────────────────────────────────
  if (window.__GLE_BOOTED__) return; // Prevent double-injection (theme re-render, app embed dupes)
  window.__GLE_BOOTED__ = true;

  // Safe storage: falls back to an in-memory Map when localStorage is unavailable,
  // full (quota exceeded), or blocked (Safari ITP / private mode / sandboxed iframe).
  const _memStore = new Map();
  let _storageOk = true;
  try {
    const t = '__gle_probe__';
    localStorage.setItem(t, '1');
    localStorage.removeItem(t);
  } catch (e) { _storageOk = false; }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
    return match ? decodeURIComponent(match[3]) : null;
  }
  function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  const safeStorage = {
    get(key) {
      if (_storageOk) {
        try {
          const val = localStorage.getItem(key);
          if (val) return val;
        } catch (e) { _storageOk = false; }
      }
      const cVal = getCookie(key);
      if (cVal) return cVal;
      return _memStore.has(key) ? _memStore.get(key) : null;
    },
    set(key, val) {
      if (_storageOk) {
        try { localStorage.setItem(key, val); } catch (e) { _storageOk = false; }
      }
      try { setCookie(key, val); } catch (e) { }
      _memStore.set(key, val);
      return true;
    }
  };

  // Network-quality awareness: degrade gracefully on slow/metered connections
  // (fewer parallel geo probes, skip speculative script preloads).
  const _conn = typeof navigator !== 'undefined' ? (navigator.connection || navigator.mozConnection || navigator.webkitConnection || null) : null;
  const isSlowNet = !!(_conn && (_conn.saveData || /2g/.test(_conn.effectiveType || '')));

  // Must be captured synchronously, right now — document.currentScript is only
  // valid during a classic script's initial (non-async) execution. Used to
  // resolve sibling language-pack JSON files sitting in the same theme assets
  // folder, without needing a hardcoded CDN path.
  const _scriptBaseUrl = (document.currentScript && document.currentScript.src) || null;

  // ── 1. SECURITY CONSTANTS & ENUMS ──────────────────────────────────────────────
  const ALLOWED_CURRENCIES = Object.freeze(['TRY', 'EUR', 'USD']);
  const ALLOWED_LANGUAGES = Object.freeze(['en', 'tr']);

  function detectLanguageFromPath(pathname) {
    const p = typeof pathname === 'string' ? pathname : (typeof window !== 'undefined' ? window.location.pathname : '');
    if (!p) return null;
    const path = p.toLowerCase().trim();
    const match = path.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
    if (!match) return null;
    const segment = match[1].toLowerCase();

    const map = {
      'en': 'en',
      'en-us': 'en',
      'en-gb': 'en',
      'en-ca': 'en',
      'en-au': 'en',
      'tr': 'tr'
    };

    if (map[segment]) return map[segment];
    if (ALLOWED_LANGUAGES.includes(segment)) return segment;
    const prefix2 = segment.slice(0, 2);
    if (map[prefix2]) return map[prefix2];
    if (ALLOWED_LANGUAGES.includes(prefix2)) return prefix2;
    return null;
  }

  function resolveInitialLanguage() {
    // 1. Explicit URL Path takes top priority (e.g. /en, /ar, /de, /fr, /es, /it, /ja, /zh-cn, /bn, /hi, /tr)
    const fromPath = detectLanguageFromPath();
    if (fromPath) return fromPath;

    // 2. Stored user preference (localStorage / cookie) - ALWAYS PRESERVE USER'S CHOICE ON NAVIGATION
    const saved = safeStorage.get('theme_selected_language');
    if (saved && ALLOWED_LANGUAGES.includes(saved)) return saved;

    // 3. Fallback for brand new first-time visitors (Turkish 'tr' is store default)
    return 'tr';
  }

  const sanitizeCurrency = code => (typeof code === 'string' && ALLOWED_CURRENCIES.includes(code.trim().toUpperCase())) ? code.trim().toUpperCase() : 'TRY';
  const sanitizeLanguage = lang => {
    if (typeof lang !== 'string') return 'tr';
    const clean = lang.trim();
    if (ALLOWED_LANGUAGES.includes(clean)) return clean;
    const mapped = detectLanguageFromPath('/' + clean);
    return mapped || 'tr';
  };
  const isValidRate = r => typeof r === 'number' && isFinite(r) && r >= 0.000001 && r <= 1000000;

  const fetchWithTimeout = async (url, timeoutMs = 2500) => {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } finally {
      clearTimeout(tid);
    }
  };

  // ── 2. LIVE EXCHANGE RATES ENGINE ─────────────────────────────────────────────
  let LIVE_RATES = { TRY: 1.0 };
  let ratesTimestamp = null;

  const CACHE_RATES_KEY = 'theme_live_exchange_rates_v6';
  const CACHE_TIME_KEY = 'theme_live_exchange_rates_time_v6';
  const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 Hours

  // Restore cached exchange rates
  try {
    const cached = safeStorage.get(CACHE_RATES_KEY);
    const cachedTime = safeStorage.get(CACHE_TIME_KEY);
    if (cached && cachedTime) {
      const parsed = JSON.parse(cached);
      const parsedTime = parseInt(cachedTime, 10);
      if (parsed && typeof parsed === 'object' && isFinite(parsedTime)) {
        ALLOWED_CURRENCIES.forEach(c => {
          if (isValidRate(parsed[c])) LIVE_RATES[c] = Number(parsed[c]);
        });
        LIVE_RATES.TRY = 1.0;
        ratesTimestamp = parsedTime;
      }
    }
  } catch (e) { }

  let ratesInFlight = null;

  const RATE_TIERS = [
    // Tier 1: Cloudflare Global CDN (Fastest, Edge delivery)
    async () => {
      const json = await fetchWithTimeout('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/try.min.json');
      if (!json || !json.try) return null;
      const out = {};
      ALLOWED_CURRENCIES.forEach(c => {
        const val = json.try[c.toLowerCase()];
        if (isValidRate(val)) out[c] = val;
      });
      return out;
    },
    // Tier 2: Cloudflare Pages Fallback
    async () => {
      const json = await fetchWithTimeout('https://latest.currency-api.pages.dev/v1/currencies/try.min.json');
      if (!json || !json.try) return null;
      const out = {};
      ALLOWED_CURRENCIES.forEach(c => {
        const val = json.try[c.toLowerCase()];
        if (isValidRate(val)) out[c] = val;
      });
      return out;
    },
    // Tier 3: Open Exchange Rates (Stable fallback)
    async () => {
      const json = await fetchWithTimeout('https://open.er-api.com/v6/latest/TRY');
      return (json && json.result === 'success' && json.rates) ? json.rates : null;
    },
    // Tier 4: ExchangeRate API (Final fallback)
    async () => {
      const json = await fetchWithTimeout('https://api.exchangerate-api.com/v4/latest/TRY');
      return (json && json.rates) ? json.rates : null;
    }
  ];

  function fetchRates(force = false) {
    const lastFetch = safeStorage.get(CACHE_TIME_KEY);
    const now = Date.now();
    const hasRate = isValidRate(LIVE_RATES[currentCurrency]);

    if (!force && lastFetch && now - parseInt(lastFetch, 10) < CACHE_TTL_MS && hasRate) {
      // Stale-while-revalidate: past 80% of TTL, kick off a silent background
      // refresh so the *next* load already has fresh numbers — the current
      // caller still gets an instant response from cache.
      const age = now - parseInt(lastFetch, 10);
      if (age > CACHE_TTL_MS * 0.8 && !ratesInFlight) {
        fetchRates(true).catch(() => { });
      }
      return Promise.resolve(LIVE_RATES);
    }

    if (ratesInFlight) return ratesInFlight;

    ratesInFlight = (async () => {
      try {
        let freshRates = null;
        for (const tierFn of RATE_TIERS) {
          try {
            freshRates = await tierFn();
            if (freshRates && typeof freshRates === 'object' && Object.keys(freshRates).length > 0) break;
          } catch (e) { /* silent fallback to next tier */ }
        }

        if (freshRates && typeof freshRates === 'object') {
          let updated = false;
          ALLOWED_CURRENCIES.forEach(c => {
            if (c !== 'TRY' && isValidRate(freshRates[c])) {
              LIVE_RATES[c] = Number(freshRates[c]);
              updated = true;
            }
          });
          LIVE_RATES.TRY = 1.0;

          if (updated) {
            ratesTimestamp = now;
            try {
              safeStorage.set(CACHE_RATES_KEY, JSON.stringify(LIVE_RATES));
              safeStorage.set(CACHE_TIME_KEY, now.toString());
            } catch (e) { }
            convertAllPrices();
            document.dispatchEvent(new CustomEvent('theme:rates:updated', { detail: { rates: Object.freeze({ ...LIVE_RATES }) } }));
          }
        }
      } catch (err) {
        console.warn('[Currency Engine] Rates fetch notice:', err);
      } finally {
        ratesInFlight = null;
      }
      return LIVE_RATES;
    })();

    return ratesInFlight;
  }

  // ── 3. CURRENCY FORMATTING & MULTI-NUMERAL PARSING ─────────────────────────────
  const CURRENCIES = Object.freeze({
    TRY: { sym: '₺', dec: 2 },
    USD: { sym: '$', dec: 2 },
    EUR: { sym: '€', dec: 2 },
  });

  const LANG_SEPS = Object.freeze({
    en: { thou: ',', dec: '.' },
    tr: { thou: '.', dec: ',' }
  });

  let currentCurrency = sanitizeCurrency(safeStorage.get('theme_selected_currency') || 'TRY');
  let currentLanguage = resolveInitialLanguage();

  function formatMoney(amount, currencyCode) {
    const curr = sanitizeCurrency(currencyCode || currentCurrency);
    const conf = CURRENCIES[curr] || CURRENCIES.USD;
    const sep = LANG_SEPS[currentLanguage] || { thou: ',', dec: '.' };
    const num = Number(amount) || 0;
    const isRound = Math.abs(num - Math.round(num)) < 0.001;

    let formatted;
    if (isRound || conf.dec === 0) {
      const intFormatted = Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, sep.thou);
      formatted = intFormatted;
    } else {
      const fixed = num.toFixed(conf.dec);
      const parts = fixed.split('.');
      const intFormatted = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep.thou);
      formatted = `${intFormatted}${sep.dec}${parts[1]}`;
    }


    return `${conf.sym}${formatted}`;
  }

  function parsePriceFromText(str) {
    if (!str || typeof str !== 'string') return null;
    let text = str
      .replace(/Turkish\s*Lira|TL|TRY|USD|EUR|[\$€₺]/gi, '')
      .replace(/[\s\u00A0\u202F]/g, '') // strip regular/non-breaking/narrow-no-break spaces used as thousands separators (e.g. French "1 234,56")
      .trim();

    if (/\d+\.\d{3},\d{1,2}/.test(text)) {
      text = text.replace(/\./g, '').replace(',', '.');
    } else if (/,\d{1,2}$/.test(text) && !/\./.test(text)) {
      text = text.replace(',', '.');
    } else {
      text = text.replace(/,/g, '');
    }
    const match = text.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : null;
  }

  // ── 4. DOM SCANNER & PRICE SHIELDING ──────────────────────────────────────────
  const PRICE_SELECTOR = [
    '.price-item', '.price-sale', '.price-regular', '.price-compare',
    '.sticky-price-sale', '.sticky-price-compare', '.sticky-price-regular',
    '.sticky-atc-price span', '.sticky-atc-price s',
    '.qam-price-sale', '.qam-price-regular', '.qam-compare',
    '.cart-item__price', '.cart-item__original-price', '.cart-item__old-price', '.cart-item__final-price',
    '.totals__total-value', '.cart-drawer .totals__total-value',
    '.search-drawer__price', '.search-drawer__price-sale', '.search-drawer__price-was', '.search-drawer__price-regular',
    '.pex-fbt__bundle-price', '.pex-fbt__bundle-compare', '.pex-fbt__check-price', '.pex-fbt__check-compare',
    '#pex-price-a', '#pex-price-b', '#pex-compare-a', '#pex-compare-b',
    '.tu-price-sale', '.tu-price-compare', '.tu-price-regular',
    '.money', '[data-price-target]', '[data-base-price]'
  ].join(', ');

  const CANDIDATE_SELECTOR = 'span, s, b, strong, em';
  const STANDALONE_PRICE_REGEX = /^(?:from\s+|başlangıç:\s*)?(?:[\$€₺]|TL|TRY|USD|EUR)\s*[\d,\.]+|\s*[\d,\.]+\s*(?:[\$€₺]|TL|TRY|USD|EUR)$/i;
  const SKIP_SELECTOR = '.no-currency-convert, .cart-drawer__rewards-bar, .rewards-track-container, .rewards-labels-row, .rewards-subtexts-row, .header__inline-menu, .mega-menu, .mega-menu__content, .cart-count-bubble, .wishlist-count-bubble, .count-bubble, .header__icon, .quantity, .quantity__input, .sticky-atc-qty, .qam-qty, .pagination, .badge, .rating, .footer__copyright-text, .not-a-price, .custom-top-bar__center, .announcement-bar, .ann-slider, .ann-slide, .announcement-bar__message, .product__title, .card__heading';

  function convertElement(el) {
    if (!el || el.nodeType !== 1 || el.classList.contains('no-currency-convert') || el.closest(SKIP_SELECTOR)) return;
    if (el.querySelector('.price-sale, .price-compare, .price-regular, .sticky-price-sale, .sticky-price-regular, .price-item, .qam-price-sale, .qam-price-regular, .qam-compare')) return;

    if (!el.classList.contains('notranslate')) el.classList.add('notranslate');
    if (el.getAttribute('translate') !== 'no') el.setAttribute('translate', 'no');

    let baseAmount;
    if (el.dataset.basePrice) {
      baseAmount = parseFloat(el.dataset.basePrice);
    } else if (el.dataset.originalBaseAmount) {
      baseAmount = parseFloat(el.dataset.originalBaseAmount);
    } else {
      const text = el.textContent || '';
      if (/from|à partir/i.test(text)) el.dataset.hasFromPrefix = 'true';
      const parsed = parsePriceFromText(text);
      if (parsed !== null && isFinite(parsed) && parsed > 0) {
        baseAmount = parsed;
        el.dataset.originalBaseAmount = parsed.toString();
      } else {
        return;
      }
    }

    if (!baseAmount || isNaN(baseAmount) || baseAmount <= 0) return;

    const hasRate = isValidRate(LIVE_RATES[currentCurrency]);
    const displayCurr = hasRate ? currentCurrency : 'TRY';
    const rate = hasRate ? LIVE_RATES[currentCurrency] : 1;
    const prefix = el.dataset.hasFromPrefix === 'true' ? 'From ' : '';
    const newText = prefix + formatMoney(baseAmount * rate, displayCurr);

    if (el.textContent !== newText) {
      el.textContent = newText;
    }
  }

  let rafId = null;
  let pendingRoots = null; // null = full-document scan queued; otherwise a Set of scoped roots
  const PENDING_ROOTS_CAP = 150; // beyond this, a scoped scan costs more than just doing a full one

  function convertAllPrices(roots) {
    if (!roots) {
      pendingRoots = null; // Full scan requested — supersedes any scoped roots queued so far
    } else if (pendingRoots !== null) {
      for (const r of roots) pendingRoots.add(r);
      if (pendingRoots.size > PENDING_ROOTS_CAP) pendingRoots = null; // fall back to full scan
    } else {
      pendingRoots = new Set(roots);
    }

    if (rafId !== null) {
      if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
      else clearTimeout(rafId);
    }
    const scheduleFn = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb) => setTimeout(cb, 16);
    rafId = scheduleFn(() => {
      rafId = null;
      const targets = pendingRoots;
      pendingRoots = null;
      scanAndConvert(targets ? Array.from(targets) : null);
    });
  }

  const _notPriceCache = new WeakMap();

  function scanAndConvert(roots) {
    const scanList = (roots && roots.length) ? roots : [document];
    try {
      scanList.forEach(root => {
        if (!root || root.nodeType === undefined) return;
        if (root.nodeType === 1 && typeof root.matches === 'function' && root.matches(PRICE_SELECTOR)) {
          convertElement(root);
        }
        if (typeof root.querySelectorAll === 'function') {
          root.querySelectorAll(PRICE_SELECTOR).forEach(convertElement);
          root.querySelectorAll(CANDIDATE_SELECTOR).forEach(el => {
            if (el.children.length !== 0 || !el.textContent) return;
            const txt = el.textContent.trim();
            if (_notPriceCache.get(el) === txt) return;
            if (txt.length > 0 && txt.length < 25 && STANDALONE_PRICE_REGEX.test(txt)) {
              convertElement(el);
            } else {
              _notPriceCache.set(el, txt);
            }
          });
        }
      });
      if (typeof window.updateCartDrawerRewardsBar === 'function') {
        window.updateCartDrawerRewardsBar();
      }
    } catch (e) {
      console.warn('[Currency Engine] Scan error:', e);
    }
  }

  // ── 5. ZERO-FLICKER GOOGLE TRANSLATE ENGINE ───────────────────────────────────
  function clearAllGoogleTranslateCookies() {
    const host = window.location.hostname;
    const domains = ['', host, '.' + host];
    const parts = host.split('.');
    if (parts.length > 2) {
      domains.push('.' + parts.slice(-2).join('.'));
    }
    const paths = ['/', ''];
    domains.forEach(d => {
      paths.forEach(p => {
        const dAttr = d ? ` domain=${d};` : '';
        const pAttr = p ? ` path=${p};` : '';
        document.cookie = `googtrans=;${pAttr}${dAttr} expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
        document.cookie = `googtrans=;${pAttr}${dAttr} expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax;`;
        document.cookie = `googtrans=;${pAttr}${dAttr} max-age=0;`;
      });
    });
  }

  function setGoogleTranslateCookie(lang) {
    const code = sanitizeLanguage(lang);
    const host = window.location.hostname;

    // 'tr' is the native source language of the store content
    if (code === 'tr') {
      clearAllGoogleTranslateCookies();
      return;
    }

    const val = `/auto/${code}`;
    const valEn = `/tr/${code}`;

    clearAllGoogleTranslateCookies();
    const domains = ['', host, '.' + host];
    const parts = host.split('.');
    if (parts.length > 2 && !/^(\d{1,3}\.){3}\d{1,3}$/.test(host)) {
      domains.push('.' + parts.slice(-2).join('.'));
    }
    domains.forEach(d => {
      const dAttr = d ? ` domain=${d};` : '';
      document.cookie = `googtrans=${val}; path=/;${dAttr} SameSite=Lax;`;
      document.cookie = `googtrans=${valEn}; path=/;${dAttr} SameSite=Lax;`;
    });
  }

  function initGoogleTranslate() {
    if (!document.getElementById('google_translate_element')) {
      const el = document.createElement('div');
      el.id = 'google_translate_element';
      el.style.cssText = 'position:absolute;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none;';
      (document.body || document.documentElement).appendChild(el);
    }

    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = function () {
        try {
          new window.google.translate.TranslateElement({
            pageLanguage: 'tr',
            includedLanguages: ALLOWED_LANGUAGES.join(','),
            autoDisplay: false
          }, 'google_translate_element');
        } catch (e) {
          console.warn('[Google Translate] Init error:', e);
        }
        ensureTranslateLanguage(currentLanguage);
      };
    }

    if (window.google?.translate?.TranslateElement) {
      window.googleTranslateElementInit();
    } else if (!document.getElementById('google-translate-script')) {
      const s = document.createElement('script');
      s.id = 'google-translate-script';
      s.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      s.async = true;
      (document.head || document.documentElement).appendChild(s);
    }

    if (!document.getElementById('google-translate-hide-styles')) {
      const style = document.createElement('style');
      style.id = 'google-translate-hide-styles';
      style.textContent = `
        .goog-te-banner-frame,
        .skiptranslate,
        iframe[id*=":1.container"],
        #goog-gt-tt,
        .goog-te-balloon-frame,
        .goog-te-spinner-pos,
        .goog-te-spinner,
        [class*="VIpgJd-ZVi9od"],
        [class*="VIpgJd"],
        .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
        .VIpgJd-ZVi9od-aZ2wEe-wOHMyf-ti6hGc,
        .VIpgJd-ZVi9od-aZ2wEe-OiiCO {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          width: 0 !important;
          opacity: 0 !important;
          pointer-events: none !important;
          position: absolute !important;
          top: -9999px !important;
          left: -9999px !important;
          z-index: -9999 !important;
        }
        body { top: 0px !important; position: static !important; }
        .goog-tooltip, .goog-tooltip:hover, #goog-gt-tt, .goog-te-balloon-frame { display: none !important; visibility: hidden !important; pointer-events: none !important; }
        .goog-text-highlight, .goog-text-highlight:hover, [class*="VIpgJd-yAWNEb"], [class*="VIpgJd"] { background-color: transparent !important; background: transparent !important; border: none !important; box-shadow: none !important; pointer-events: none !important; }
        font, font * { font-family: inherit !important; font-size: inherit !important; font-weight: inherit !important; line-height: inherit !important; letter-spacing: inherit !important; color: inherit !important; background: transparent !important; vertical-align: baseline !important; display: inline !important; pointer-events: none !important; }
        .translated-ltr, .translated-rtl, .translated-ltr body, .translated-rtl body { font-family: var(--font-body-family, 'Poppins', 'Hind Siliguri', sans-serif) !important; }
      `;
      (document.head || document.documentElement).appendChild(style);
    }
  }

  function ensureTranslateLanguage(lang, attempts = 0) {
    const target = sanitizeLanguage(lang);
    setGoogleTranslateCookie(target);

    if (!window.google?.translate?.TranslateElement && !document.getElementById('google-translate-script')) {
      initGoogleTranslate();
    }

    const select = document.querySelector('.goog-te-combo');
    if (select && select.options && select.options.length > 0) {
      let matchedIndex = -1;
      let matchedValue = '';

      for (let i = 0; i < select.options.length; i++) {
        const optVal = (select.options[i].value || '').trim();
        if (target === 'tr') {
          if (optVal === '' || optVal.toLowerCase() === 'tr') {
            matchedIndex = i;
            matchedValue = optVal;
            break;
          }
        } else {
          if (optVal.toLowerCase() === target.toLowerCase()) {
            matchedIndex = i;
            matchedValue = optVal;
            break;
          }
          if (optVal.toLowerCase().startsWith(target.toLowerCase() + '-') || target.toLowerCase().startsWith(optVal.toLowerCase())) {
            matchedIndex = i;
            matchedValue = optVal;
          }
        }
      }

      if (matchedIndex >= 0) {
        select.selectedIndex = matchedIndex;
        select.value = matchedValue;
      } else {
        select.value = target === 'tr' ? '' : target;
      }

      try {
        if (typeof select.onchange === 'function') {
          select.onchange();
        }
      } catch (e) {}

      select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));

      // If target is Turkish (original source), trigger restore UI if present
      if (target === 'tr') {
        try {
          const iframe = document.querySelector('.goog-te-banner-frame, iframe[id*=":1.container"]');
          if (iframe && iframe.contentDocument) {
            const restoreBtn = iframe.contentDocument.querySelector('.goog-te-button button, #goog-gt-tt button');
            if (restoreBtn) restoreBtn.click();
          }
        } catch (e) {}
      }
    } else if (attempts < 80) {
      setTimeout(() => ensureTranslateLanguage(target, attempts + 1), 75);
    }
  }

  // ── 5.1 İSTEMCİ TARAFI SÖZLÜK (KALDIRILDI) ───────────────────────────────────
  // Tema artık Shopify'ın kendi çeviri altyapısını kullanıyor (tr.default.json /
  // en.json). Buradaki çok dilli renk/arayüz sözlüğü ve arama indeksi hiçbir yerden
  // çağrılmıyordu; kaldırıldı.

  function getInstantTranslation(text, targetLang) {
    // The site now runs in Turkish only, with no automatic translation in
    // any direction \u2014 whatever text is actually stored (product data,
    // theme strings) is shown exactly as-is.
    return text;
  }

  // ── 5.2 ELEMENT AĞACI ÇEVİRİCİ (YALNIZCA FİYAT DÖNÜŞÜMÜ) ─────────────────────
  // Metin çevirisi artık Shopify'ın çeviri dosyalarıyla sunucu tarafında yapılıyor.
  // Eski istemci tarafı metin değiştirme, data-raw-text değerleri küçük harfli
  // olduğu için etiketlerin büyük/küçük harf düzenini bozuyordu; kaldırıldı.
  // Geriye dönük uyumluluk için fonksiyon korunuyor ve yalnızca fiyatları dönüştürüyor.
  function translateElementTree(rootNode) {
    if (!rootNode) return;
    scanAndConvert([rootNode]);
  }

  const _ric = typeof requestIdleCallback === 'function'
    ? (fn) => requestIdleCallback(fn, { timeout: 300 })
    : (fn) => setTimeout(fn, 0);

  function updateAllSelectedValues(lang, scopedRoot) {
    translateElementTree(scopedRoot && scopedRoot !== document.body ? scopedRoot : document.body);
  }

  // ── 6. GEOLOCATION (DISABLED - TURKISH PRIMARY DEFAULT) ───────────────────────
  function detectCountry() {
    return Promise.resolve('TR');
  }

  // ── 7. UI SYNCHRONIZATION ─────────────────────────────────────────────────────
  function updateUI() {
    document.querySelectorAll('[data-ctb-selector]').forEach(selector => {
      const type = selector.dataset.ctbSelector;
      const currentVal = type === 'currency' ? currentCurrency : currentLanguage;
      const attr = type === 'currency' ? 'data-currency' : 'data-lang';

      selector.querySelectorAll('.ctb-dropdown__item').forEach(item => {
        const match = item.getAttribute(attr) === currentVal;
        item.classList.toggle('is-active', match);
        if (match) {
          const flag = item.querySelector('.ctb-flag');
          const text = item.querySelector('span:last-child');
          const targetFlag = selector.querySelector('[data-ctb-current-flag]');
          const targetText = selector.querySelector('[data-ctb-current-text]');
          if (flag && targetFlag) targetFlag.innerHTML = flag.innerHTML;
          if (text && targetText) {
            if (type === 'currency') {
              targetText.textContent = currentVal;
            } else {
              targetText.textContent = text.textContent.trim();
            }
          }
        }
      });
    });
  }

  // ── 8. PUBLIC API ─────────────────────────────────────────────────────────────
  const GlobalLocalization = Object.freeze({
    getCurrency: () => currentCurrency,
    setCurrency: (code) => {
      const clean = sanitizeCurrency(code);
      currentCurrency = clean;
      try {
        safeStorage.set('theme_selected_currency', clean);
        safeStorage.set('theme_manual_currency', 'true');
      } catch (e) { }
      convertAllPrices();
      updateUI();
      document.dispatchEvent(new CustomEvent('theme:currency:changed', { detail: { currency: clean } }));
    },
    getLanguage: () => currentLanguage,
    setLanguage: (lang) => {
      const clean = sanitizeLanguage(lang);
      currentLanguage = clean;
      try {
        safeStorage.set('theme_selected_language', clean);
        safeStorage.set('theme_manual_language', 'true');
      } catch (e) { }

      convertAllPrices();
      updateUI();
      updateAllSelectedValues(clean);

      if (clean === 'tr') {
        clearAllGoogleTranslateCookies();
        ensureTranslateLanguage('tr');
      } else {
        initGoogleTranslate();
        ensureTranslateLanguage(clean);
      }

      document.dispatchEvent(new CustomEvent('theme:language:changed', { detail: { language: clean } }));
    },
    getRates: () => Object.freeze({ ...LIVE_RATES }),
    getRate: (curr) => {
      const c = sanitizeCurrency(curr);
      return isValidRate(LIVE_RATES[c]) ? LIVE_RATES[c] : 1;
    },
    hasLiveRate: (curr) => isValidRate(LIVE_RATES[sanitizeCurrency(curr)]),
    getRatesMeta: () => Object.freeze({
      lastUpdated: ratesTimestamp,
      ageMs: ratesTimestamp ? (Date.now() - ratesTimestamp) : null,
      currenciesLoaded: Object.keys(LIVE_RATES).length,
      isComplete: Object.keys(LIVE_RATES).length === ALLOWED_CURRENCIES.length
    }),
    refreshRates: () => fetchRates(true),
    detectLocation: detectCountry,
    convertAllPrices: convertAllPrices,
    formatMoney: formatMoney,
    getInstantTranslation: getInstantTranslation,
    updateAllSelectedValues: updateAllSelectedValues,
    translateElementTree: translateElementTree
  });

  window.GlobalLocalization = GlobalLocalization;

  // ── 9. INITIALIZATION & OBSERVERS ─────────────────────────────────────────────
  async function init() {
    // Dil, Shopify'ın aktif storefront diline göre belirlenir (varsayılan: Türkçe).
    // İstemci tarafı otomatik çeviri kullanılmaz; metinler tema çeviri dosyalarından
    // (tr.default.json / en.json) gelir. Para birimi dönüşümü dilden bağımsız çalışır.
    // Kayıtlı kullanıcı tercihi (currentLanguage, resolveInitialLanguage() ile
    // zaten belirlendi) korunur; sayfa geçişlerinde tekrar Türkçe'ye dönmesin.
    if (currentLanguage === 'en') {
      initGoogleTranslate();
      ensureTranslateLanguage('en');
    } else {
      clearAllGoogleTranslateCookies();
    }

    // 1. Synchronous, instant local UI translation (0ms latency, zero flicker)
    updateUI();
    convertAllPrices();

    // 3. Background live exchange rates fetch (non-blocking)
    fetchRates().then(() => {
      convertAllPrices();
    }).catch(() => {});

    // Global click delegation for top bar & mobile menu drawer dropdowns
    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('.ctb-dropdown__toggle, .ctb-selector__btn, .menu-drawer__currency-btn, .menu-drawer__language-btn');
      const itemBtn = e.target.closest('.ctb-dropdown__item');

      if (toggleBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const parent = toggleBtn.closest('[data-ctb-selector]');
        if (parent) {
          const wasOpen = parent.classList.contains('is-open');
          document.querySelectorAll('[data-ctb-selector].is-open').forEach(s => {
            s.classList.remove('is-open');
            const b = s.querySelector('button');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
          if (!wasOpen) {
            parent.classList.add('is-open');
            toggleBtn.setAttribute('aria-expanded', 'true');
          }
        }
        return;
      }

      if (itemBtn) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const parent = itemBtn.closest('[data-ctb-selector]');
        const type = parent ? parent.dataset.ctbSelector : null;

        document.querySelectorAll('[data-ctb-selector].is-open').forEach(s => {
          s.classList.remove('is-open');
          const b = s.querySelector('button');
          if (b) b.setAttribute('aria-expanded', 'false');
        });

        if (type === 'currency' || itemBtn.dataset.currency) {
          const curr = itemBtn.dataset.currency;
          if (curr) GlobalLocalization.setCurrency(curr);
        } else if (type === 'lang' || itemBtn.dataset.lang) {
          const lang = itemBtn.dataset.lang;
          if (lang) GlobalLocalization.setLanguage(lang);
        }
        return;
      }

      document.querySelectorAll('[data-ctb-selector].is-open').forEach(s => {
        s.classList.remove('is-open');
        const b = s.querySelector('button');
        if (b) b.setAttribute('aria-expanded', 'false');
      });
    });

    let mutationRoots = new Set();
    let mutationTimer = null;
    const MASSIVE_UPDATE_THRESHOLD = 150;

    const observer = new MutationObserver((mutations) => {
      for (let i = 0; i < mutations.length; i++) {
        const m = mutations[i];
        if (m.target) {
          const tag = m.target.tagName;
          if (tag === 'FONT' || tag === 'SCRIPT' || tag === 'STYLE' || tag === 'HEAD') continue;
          if (m.target.closest?.('.cart-drawer__rewards-bar')) continue;
          if (m.target.id?.startsWith('goog') || m.target.classList?.contains('skiptranslate') || m.target.closest?.('#google_translate_element, [class*="VIpgJd"], .goog-te-banner-frame, .skiptranslate')) continue;
        }

        const added = m.addedNodes;
        for (let j = 0; j < added.length; j++) {
          const node = added[j];
          if (node.nodeType === 1) {
            const nodeTag = node.tagName;
            if (nodeTag === 'FONT' || nodeTag === 'SCRIPT' || nodeTag === 'STYLE' || nodeTag === 'IFRAME') continue;
            if (node.classList?.contains('skiptranslate') || node.id?.startsWith('goog') || (node.hasAttribute?.('translate') && node.getAttribute('translate') === 'no')) continue;
            mutationRoots.add(node);
            if (mutationRoots.size > MASSIVE_UPDATE_THRESHOLD) break;
          }
        }
        if (mutationRoots.size > MASSIVE_UPDATE_THRESHOLD) break;
      }

      if (!mutationRoots.size) return;

      if (mutationTimer) cancelAnimationFrame(mutationTimer);
      mutationTimer = requestAnimationFrame(() => {
        mutationTimer = null;
        const capturedRoots = Array.from(mutationRoots);
        mutationRoots.clear();
        const isMassiveUpdate = capturedRoots.length > MASSIVE_UPDATE_THRESHOLD;
        // Text is already Turkish, so no per-mutation translation scan is
        // needed here anymore — only currency conversion still runs.
        convertAllPrices(isMassiveUpdate ? undefined : capturedRoots);
      });
    });
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }

    // Fast lightweight rescan on dynamic DOM events (only converts prices, avoids full document text re-traversal)
    // Sepetle ilgili yeniden taramalar yalnızca sepet alanlarında yapılır. Tüm sayfayı taramak
    // (özellikle çok ürünlü koleksiyon sayfalarında) sepet açılıp kapanırken telefonda kasma yapıyordu.
    const CART_ROOTS_SELECTOR = 'cart-drawer, #cart-icon-bubble, cart-notification, cart-items, #main-cart-footer';
    const triggerRescan = (delay = 50, cartOnly = false) => {
      setTimeout(() => {
        _ric(() => {
          if (cartOnly) {
            const roots = Array.from(document.querySelectorAll(CART_ROOTS_SELECTOR));
            if (roots.length) {
              convertAllPrices(roots);
              return;
            }
          }
          convertAllPrices();
        });
      }, delay);
    };

    document.addEventListener('variant:changed', () => triggerRescan(30));
    document.addEventListener('cart:refresh', () => triggerRescan(50, true));
    document.addEventListener('quick-add:opened', () => triggerRescan(30));
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => triggerRescan(50, true));
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('#cart-icon-bubble, .header__icon--cart, .cart-drawer, .cart-drawer__close')) {
        // Sepet çekmecesi açılıp kapanırken (kayma animasyonu ≈0.45 sn) fiyat taraması çalışmasın:
        // tarama tüm çekmecedeki span/strong/em elemanlarını gezip CSS eşleştirmesi yapıyor ve animasyonun
        // tam ortasına denk gelip telefonda takılma yapıyordu. Animasyon bittikten sonra çalışır.
        // (Sepet içeriği değişirse zaten cartUpdate / MutationObserver yolları 50 ms'de ayrıca tarıyor.)
        triggerRescan(700, true);
      } else if (e.target.closest('.header__search summary')) {
        triggerRescan(50);
      }
    });

    // Cross-tab sync: if the shopper changes currency/language in one tab
    // (or another tab's background refresh lands new rates), reflect it here
    // instantly instead of waiting for this tab's own TTL to expire.
    window.addEventListener('storage', (e) => {
      if (!e.key) return;
      if (e.key === 'theme_selected_currency' && e.newValue) {
        const clean = sanitizeCurrency(e.newValue);
        if (clean !== currentCurrency) {
          currentCurrency = clean;
          convertAllPrices();
          updateUI();
        }
      } else if (e.key === 'theme_selected_language' && e.newValue) {
        const clean = sanitizeLanguage(e.newValue);
        if (clean !== currentLanguage) {
          currentLanguage = clean;
          convertAllPrices();
          updateUI();
          updateAllSelectedValues(clean);
          ensureTranslateLanguage(clean);
        }
      } else if (e.key === CACHE_RATES_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed && typeof parsed === 'object') {
            ALLOWED_CURRENCIES.forEach(c => { if (isValidRate(parsed[c])) LIVE_RATES[c] = Number(parsed[c]); });
            LIVE_RATES.TRY = 1.0;
            convertAllPrices();
          }
        } catch (err) { }
      }
    });

    // bfcache / long-hidden-tab resume: rates and geo can go stale while a tab
    // sits backgrounded for hours; re-validate (cheap, cache-checked) on return
    // rather than waiting for the next natural page load.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        fetchRates().catch(() => { });
      }
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        fetchRates().catch(() => { });
        convertAllPrices();
        updateUI();
      }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();